import {
    useExportOutgoingPostbacksMutation,
    useGetOutgoingPostbacksQuery,
} from "api/TenantSlug/OutgoingPostback/hooks.ts";
import { AddTrackingToolToDrawer } from "components/Drawers/AddTrackingToolToDrawer/AddTrackingToolToDrawer";
import { useConversionTypeSelectOptions } from "components/UI/Selects/ConversionTypeSelectSearchable/useConversionTypeSelectOptions.ts";
import { useSourceSelectOptions } from "components/UI/Selects/SourceSelectSearchable/useSourceSelectOptions.ts";
import { ShortenedIdWithCopy } from "components/UI/ShortenedIdWithCopy";
import { Table } from "components/UI/Table";
import { exportAtom } from "components/UI/Table/atoms/exportAtom.ts";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, tableAtom } from "components/UI/Table/atoms/tableAtom.ts";
import { TableColumn } from "components/UI/Table/columns/TableColumn.tsx";
import { FILTER_TYPES } from "components/UI/Table/constants/filter.ts";
import { useTableDataApiOperations } from "components/UI/Table/hooks/useTableDataApiOperations";
import { TableColumnType } from "components/UI/Table/types/table.ts";
import { ColumnHeaderTitle } from "components/UI/Table/UI/ColumnHeaderTitle";
import { CountryCell } from "components/UI/Table/UI/CountryCell/CountryCell";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions.ts";
import { useSmartRowHandlers } from "hooks/useTableRowHandlers.tsx";
import { useRecoilValue } from "recoil";
import { PermissionEnum } from "types/enums";
import { DrawerId, TableId } from "types/models.ts";

import OutgoingPostbackData = App.Data.OutgoingPostbackData;
import GetOutgoingPostbacksResponse = App.Http.Responses.Tracking.GetOutgoingPostbacksResponse;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;

export const TrackingToolsToTable = () => {
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const { t: tC } = usePaldockTranslation("common");
    const tableId = TableId.TrackingToolsTo;
    const tableAtomInstance = tableAtom<OutgoingPostbackData>();
    const tableState = useRecoilValue(tableAtomInstance(tableId));
    const permissionUtils = usePermission();
    const canViewOwner = permissionUtils.hasPermission(PermissionEnum.OutgoingPostbackViewOwner);
    const exportState = useRecoilValue(exportAtom(tableId));
    const { options: sourceOptions } = useSourceSelectOptions();
    const { options: conversionTypeOptions } = useConversionTypeSelectOptions();

    const request = {
        filter: {
            filter: {
                ...(tableState.filter?.filter || {}),
            },
            include: `types,sources,country,offers,excludedOffers,affiliates,advertisers${canViewOwner ? ",owner" : ""}`,
            sort: tableState?.filter?.sort ?? "name",
        },
        page: {
            number: tableState.page?.number ?? DEFAULT_PAGE_NUMBER,
            size: tableState.page?.size ?? DEFAULT_PAGE_SIZE,
        },
    };

    const query = useGetOutgoingPostbacksQuery(request, { enabled: false });
    const exportOutgoingPostbacksMutation = useExportOutgoingPostbacksMutation();

    const tableFetchDataUtils = useTableDataApiOperations<
        GetOutgoingPostbacksResponse,
        void,
        Record<string, never>,
        BaseExportRequest
    >(tableId, query, undefined, undefined, exportOutgoingPostbacksMutation);

    const columnConfig: TableColumnType<OutgoingPostbackData>[] = [
        {
            title: <ColumnHeaderTitle columnTitle={tG("name")} />,
            dataIndex: "name",
            key: "name",
            sorter: true,
            defaultSorter: true,
            ellipsis: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            width: 200,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("source")} />,
            dataIndex: "sources",
            key: "sources",
            render: (sources?: string[]) => {
                return sources?.length ? sources.join(", ") : "-";
            },
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.select,
            filterName: "source",
            filterOptions: sourceOptions,
            ellipsis: true,
            width: 140,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("type")} />,
            dataIndex: "types",
            key: "types",
            render: (types?: Array<{ title: string }>) =>
                types?.length ? types.map((type) => type.title).join(", ") : "-",
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.select,
            filterName: "type",
            filterOptions: conversionTypeOptions,
            ellipsis: true,
            width: 140,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("country")} />,
            dataIndex: "country_id",
            key: "country_id",
            render: (_, data: OutgoingPostbackData) => <CountryCell country={data.country} />,
            sorter: true,
            width: 100,
            filterable: true,
            filterType: FILTER_TYPES.countrySelect,
        },
        /* {
            title: <ColumnHeaderTitle columnTitle="Kategorie" />,
            dataIndex: "category",
            key: "category",
        },*/
        TableColumn.Entity.Affiliates<OutgoingPostbackData>({
            title: <ColumnHeaderTitle columnTitle={tG("affiliate")} />,
            emptyLabel: tC("all"),
            width: 140,
            getEntity: (postback) => postback.affiliates ?? [],
        }),
        TableColumn.Entity.Advertisers<OutgoingPostbackData>({
            title: <ColumnHeaderTitle columnTitle={tG("advertiser")} />,
            emptyLabel: tC("all"),
            width: 140,
            getEntity: (postback) => postback.advertisers ?? [],
        }),
        ...(canViewOwner
            ? [
                  TableColumn.Entity.Owner<OutgoingPostbackData>({
                      title: <ColumnHeaderTitle columnTitle={tG("owner")} />,
                      dataIndex: "owner.nickname",
                      key: "owner.nickname",
                      filterName: "owner_id",
                      sortName: "owner.nickname",
                      ellipsis: true,
                      width: 150,
                      getEntity: (row) => row.owner,
                  }),
              ]
            : []),
        {
            title: <ColumnHeaderTitle columnTitle={tG("offer")} />,
            dataIndex: "offers",
            key: "offers",
            render: (offers?: Array<App.Data.OfferData>) =>
                !offers || offers.length === 0 ? tC("all") : offers.map((offer) => offer.title).join(", "),
            filterable: true,
            filterType: FILTER_TYPES.offerSelect,
            filterName: "offers.id",
            filterIncludeNull: true,
            filterNullOptionLabel: tC("all"),
            ellipsis: true,
            width: 140,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("id")} />,
            dataIndex: "id",
            key: "id",
            render: (id) => <ShortenedIdWithCopy id={id} />,
            ellipsis: true,
            width: 160,
            filterable: true,
            filterType: FILTER_TYPES.string,
            sorter: true,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("state")} />,
            dataIndex: "is_active",
            key: "is_active",
            render: (isActive) => {
                const isActiveBool = isActive === true;
                return (
                    <div className="flex items-center gap-2">
                        <span
                            className={`inline-block w-2 h-2 rounded-lg ${isActiveBool ? "bg-green-500" : "bg-mediumRed2"}`}
                        />
                        <span className="text-sm">{isActiveBool ? tC("active") : tC("paused")}</span>
                    </div>
                );
            },
            // TODO - BE allow sort
            // sorter: true,
            ellipsis: true,
            filterable: true,
            filterType: FILTER_TYPES.select,
            filterOptions: [
                { label: tC("active"), value: true },
                { label: tC("paused"), value: false },
            ],
            width: 100,
            sorter: true,
        },
    ];

    const { getRowHandlers, getRowClassName } = useSmartRowHandlers<OutgoingPostbackData>(
        {
            entityName: "outgoingPostback",
            editPermission: PermissionEnum.OutgoingPostbackUpdate,
            detailPermission: PermissionEnum.OutgoingPostbackView,
            editUrl: "/$tenantSlug/tool/tracking/tracking-api/:id/general",
        },
        {
            isRowClickable:
                permissionUtils.hasPermission(PermissionEnum.ToolTrackingOutgoingEditEditor) ||
                permissionUtils.hasPermission(PermissionEnum.ToolTrackingOutgoingEditGeneral),
        }
    );

    return (
        <>
            {permissionUtils.hasPermission(PermissionEnum.OutgoingPostbackCreate) && (
                <AddTrackingToolToDrawer onSubmit={() => tableFetchDataUtils.refetchData()} />
            )}
            <Table<GetOutgoingPostbacksResponse, OutgoingPostbackData>
                tableId={tableId}
                data={tableFetchDataUtils.tableData as GetOutgoingPostbacksResponse}
                dataSource={tableFetchDataUtils.tableData?.outgoingPostbacks || []}
                columnConfig={columnConfig}
                loading={tableFetchDataUtils.fetchQuery.isLoading || tableFetchDataUtils.fetchQuery.isFetching}
                rowKey={(record) => record.id}
                addEntityData={{
                    drawerId: DrawerId.AddTrackingToolTo,
                    permission: PermissionEnum.OutgoingPostbackCreate,
                }}
                onRefresh={tableFetchDataUtils.refetchData}
                showRefetchButton={true}
                onRow={(record, rowIndex) => getRowHandlers(record, rowIndex)}
                rowClassName={(_, rowIndex) => getRowClassName(rowIndex)}
                onExport={() =>
                    tableFetchDataUtils.exportData({
                        extension: exportState.extension,
                        headers: exportState.headers,
                        export_all: exportState.range === "all",
                        page: request.page,
                        filter: request.filter,
                        delimiter: exportState.delimiter,
                    })
                }
                enableFilters={true}
                searchDataIndex="name"
                hideActionsColumn={true}
                autoScrollWidth={true}
            />
        </>
    );
};
